import React, { useState, useEffect } from 'react';
import { FileText, Settings, ShieldHalf, ClipboardCheck, ChartLine, Loader2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import jsPDF from 'jspdf';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';

const REPORTS = [
    {
        key: 'Technical',
        label: 'Technical Report',
        icon: Settings,
        description: 'Detailed technical findings with remediation code snippets, configuration fixes, and implementation timelines for IT teams.',
        items: ['Vulnerabilities', 'Config Issues', 'Code Fixes', 'Testing Steps']
    },
    {
        key: 'CISORisk',
        label: 'CISO Risk Report',
        icon: ShieldHalf,
        description: 'Risk-prioritized findings with threat context, business impact quantification, and strategic recommendations.',
        items: ['Risk Matrix', 'Threat Intel', 'Impact $', 'Roadmap']
    },
    {
        key: 'ExecSummary',
        label: 'Executive Summary',
        icon: ChartLine,
        description: 'Business-focused overview with key metrics, investment recommendations, and competitive positioning insights.',
        items: ['KPIs', 'Budget Ask', 'Timeline', 'ROI']
    },
    {
        key: 'AuditDoc',
        label: 'Audit Documentation',
        icon: ClipboardCheck,
        description: 'Audit-ready evidence packages with control mappings, test results, and compliance attestation language.',
        items: ['Evidence', 'Controls', 'Attestations', 'SSP']
    }
];

const ReportGenTab = ({ scanId }) => {
    const [selectedReports, setSelectedReports] = useState([]);
    const [reportStates, setReportStates] = useState({}); // { [key]: { loading, error, cached, meta, expanded } }
    const [generating, setGenerating] = useState(false);

    // Toggle report selection (card click)
    const toggleReport = (key) => {
        setSelectedReports((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    // Toggle dropdown for a report
    const toggleDropdown = (key, e) => {
        e.stopPropagation();
        setReportStates((prev) => ({
            ...prev,
            [key]: { ...prev[key], expanded: !prev[key]?.expanded }
        }));
    };

    // On mount, fetch cache for all reports
    useEffect(() => {
        const fetchCachedReports = async () => {
            const newStates = {};
            for (const report of REPORTS) {
                try {
                    const cachedRes = await fetch(`${ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Report Gen")}?reportType=${report.key}`, { method: 'POST', credentials: 'include' });
                    if (cachedRes.status === 200) {
                        const json = await cachedRes.json();
                        const data = JSON.parse(json.response);
                        newStates[report.key] = {
                            loading: false,
                            cached: true,
                            meta: {
                                tokensUsed: json.tokens_used,
                                costUsd: json.cost_usd,
                                lastUpdated: json.lastUpdated,
                                cached: true
                            },
                            expanded: false
                        };
                    } else {
                        newStates[report.key] = { loading: false, cached: false, expanded: false };
                    }
                } catch {
                    newStates[report.key] = { loading: false, cached: false, expanded: false };
                }
            }
            setReportStates(newStates);
        };
        fetchCachedReports();
    }, [scanId]);

    // Download PDF using jsPDF
    const downloadPdf = (title, content) => {
        const doc = new jsPDF();

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const xMargin = 15;
        const topMargin = 20;
        const bottomMargin = 20;
        const maxPageHeight = pageHeight - bottomMargin;
        
        // 1. Set Title
        doc.setFontSize(16);
        doc.text(title, xMargin, topMargin);
        
        // 2. Prepare Body Content
        doc.setFontSize(12);
        const textWidth = pageWidth - (xMargin * 2); // Calculate usable width
        const lines = doc.splitTextToSize(content, textWidth); // Split text into lines
        
        // Calculate line height (fontSize is in points, convert to doc units roughly)
        // 12pt is roughly 5mm-7mm depending on spacing. doc.getLineHeight() is safer.
        const lineHeight = doc.getLineHeight() * 0.3528; // Convert pt to mm (default unit)
        
        let yPos = 35; // Starting position for the body text

        lines.forEach((line) => {
            // Check if the next line will exceed the page height
            if (yPos + lineHeight > maxPageHeight) {
                doc.addPage();
                yPos = topMargin; // Reset to top margin on new page
            }
            
            doc.text(line, xMargin, yPos);
            yPos += lineHeight; // Move cursor down for next line
        });

        doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
    };

    // Fetch and download a single report (cache first, then generate)
    const fetchAndDownloadReport = async (report, forceRegenerate = false) => {
        setReportStates((prev) => ({ ...prev, [report.key]: { ...prev[report.key], loading: true, error: null } }));
        try {
            let json, data, meta = {};
            if (!forceRegenerate) {
                // Try cache
                const cachedRes = await fetch(`${ApiNames.AI_HUB_FETCH_CACHED_AI_DATA(scanId, "Report Gen")}?reportType=${report.key}`, { method: 'POST', credentials: 'include' });
                if (cachedRes.status === 200) {
                    json = await cachedRes.json();
                    data = JSON.parse(json.response);
                    meta = { tokensUsed: json.tokens_used, costUsd: json.cost_usd, lastUpdated: json.lastUpdated, cached: true };
                    downloadPdf(report.label, data.output);
                    setReportStates((prev) => ({ ...prev, [report.key]: { ...prev[report.key], loading: false, cached: true, meta } }));
                    return;
                }
            }
            // Generate
            const genRes = await fetch(`${ApiNames.AI_HUB_REPORT_GEN(scanId, report.key)}`, { method: 'POST', credentials: 'include' });
            if (!genRes.ok) throw new Error('Failed to generate report');
            json = await genRes.json();
            data = JSON.parse(json.response);
            meta = { tokensUsed: json.tokens_used, costUsd: json.cost_usd, lastUpdated: "Now", cached: false };
            downloadPdf(report.label, data.output);
            setReportStates((prev) => ({ ...prev, [report.key]: { ...prev[report.key], loading: false, cached: false, meta } }));
        } catch (err) {
            setReportStates((prev) => ({ ...prev, [report.key]: { ...prev[report.key], loading: false, error: 'Unable to generate or fetch report.' } }));
        }
    };

    // Generate all selected reports
    const handleDownloadSelected = async () => {
        setGenerating(true);
        await Promise.all(selectedReports.map(async (key) => {
            const report = REPORTS.find((r) => r.key === key);
            await fetchAndDownloadReport(report);
        }));
        setGenerating(false);
    };

    // Regenerate a specific report
    const handleRegenerate = async (report) => {
        await fetchAndDownloadReport(report, true);
    };

    return (
        <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-6xl">
                <div className="card-header flex items-center justify-between mb-2">
                    <div className="card-title flex items-center gap-2">
                        <FileText className="w-6 h-6 text-blue-400" />
                        <h3 className="font-bold text-lg">AI Report Generator</h3>
                    </div>
                    <span className="card-badge flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
                        <FileText className="w-4 h-4 mr-1" />AI-Powered
                    </span>
                </div>
                <p className="text-gray-400 mb-6 text-sm mt-2">
                    Select one or more report types to download. Once generated, reports are cached for speed and cost savings, but can be regenerated. Download as PDF.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {REPORTS.map((report) => {
                        const Icon = report.icon;
                        const state = reportStates[report.key] || {};
                        const isSelected = selectedReports.includes(report.key);
                        return (
                            <div
                                key={report.key}
                                className={`report-card flex flex-col items-center p-6 rounded-xl border-2 transition-all duration-200 shadow-sm h-full bg-base-100 relative cursor-pointer ${isSelected ? 'border-blue-500 scale-105' : 'border-base-300 hover:border-blue-400'}`}
                                onClick={() => toggleReport(report.key)}
                                tabIndex={0}
                                role="button"
                                aria-pressed={isSelected}
                            >
                                <span className="report-icon mb-2">
                                    <Icon className="w-8 h-8" />
                                </span>
                                <h4 className="font-semibold text-base mb-1 text-center">{report.label}</h4>
                                <p className="text-xs text-gray-500 text-center mt-1 mb-3">{report.description}</p> 
                                <div className="flex flex-wrap text-xs text-gray-400 text-center gap-x-2">
                                    {report.items.map((item) => {
                                        return (
                                            <div className="flex items-center h-8 bg-gray-200 rounded mb-2">
                                                <p className="text-center w-full whitespace-nowrap m-2">{item}</p>
                                            </div>
                                        )
                                    })}
                                </div>
                                <button
                                    className={`absolute bottom-2 right-2 p-1 rounded-full hover:bg-base-200 transition-colors z-10`}
                                    onClick={e => toggleDropdown(report.key, e)}
                                    type="button"
                                    aria-label="Show report details"
                                    tabIndex={0}
                                >
                                    {state.expanded ? <ChevronUp className="w-5 h-5 text-blue-400" /> : <ChevronDown className="w-5 h-5 text-blue-400" />}
                                </button>
                                {state.expanded && (
                                    <div className="w-full mt-4 bg-base-200 rounded-lg p-3 text-xs text-left animate-fadeIn z-20">
                                        {state.loading ? (
                                            <div className="flex items-center gap-2"><Loader2 className="animate-spin w-4 h-4 text-blue-400" /> Loading...</div>
                                        ) : state.error ? (
                                            <div className="text-red-400">{state.error}</div>
                                        ) : state.meta ? (
                                            <>
                                                <div className="mb-1"><b>Status:</b> {state.cached ? 'Cached' : 'Freshly Generated'}</div>
                                                <div className="mb-1"><b>Tokens Used:</b> {state.meta.tokensUsed}</div>
                                                <div className="mb-1"><b>Cost:</b> ${state.meta.costUsd}</div>
                                                <div className="mb-1"><b>Last Updated:</b> {state.meta.lastUpdated}</div>
                                                <button
                                                    className="mt-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded shadow flex items-center gap-2"
                                                    onClick={e => { e.stopPropagation(); handleRegenerate(report); }}
                                                    disabled={state.loading}
                                                >
                                                    <RefreshCw className="w-4 h-4" /> Regenerate Report
                                                </button>
                                            </>
                                        ) : (
                                            <div className="text-gray-400">No data yet. Select this report and start the download process to see details.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-col items-center py-6">
                    <button
                        className="generate-btn bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-lg"
                        onClick={handleDownloadSelected}
                        disabled={generating || selectedReports.length === 0}
                    >
                        {generating ? <Loader2 className="animate-spin w-6 h-6" /> : <FileText className="w-6 h-6" />}
                        Download Selected Reports
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportGenTab;
